import { Module } from 'vuex';
import reportsRepository from '@/modules/reports/shared/repositories/reports.repository';
import { Report } from '@/modules/reports/shared/domain/report';
import { ReportsManagementState } from '@/modules/reports/management/store/reports-management.state';
import { ReportsManagementGetters } from '@/modules/reports/management/store/reports-management.getters';
import { ReportsManagementMutations } from '@/modules/reports/management/store/reports-management.mutations';
import { ReportsManagementActions } from '@/modules/reports/management/store/reports-management.actions';
import { Exception } from '@/modules/shared/shared/domain/exceptions/exception';
import { EquipmentAvailability } from '@/modules/reports/management/domain/equipment-availability';
import { AvailabilityRegistryView } from '@/modules/reports/management/domain/availability-registry.view';
import { IncidentsView } from '@/modules/reports/management/domain/incidents.view';
import { Incident } from '@/modules/reports/shared/domain/incident';
import incidentsRepository from '@/modules/reports/shared/repositories/incidents.repository';
import { ReportsHistoryDatatableItem } from '@/modules/reports/management/domain/reports-history-datatable';
import moment from 'moment';
import { IncidentsHistoryDatatableItem } from '@/modules/reports/management/domain/incidents-history-datatable.item';
import { HistoryState } from '@/modules/reports/management/domain/history.state';
import { downloadFile } from '@/modules/shared/shared/domain/functions/file-downloader';

const reportsManagement: Module<ReportsManagementState, any> = {
    namespaced: true,
    state: {
        availabilityRegistryView: {
            locations: [],
        },
        incidentRegistryView: {
            locations: [],
            causes: [],
        },
        historyView: {
            reports: {
                start: 0,
                limit: 0,
                total: 0,
                pages: 0,
                results: [],
            },
            incidents: {
                start: 0,
                limit: 0,
                total: 0,
                pages: 0,
                results: [],
            },
        },
    },
    getters: {
        [ReportsManagementGetters.GET_AVAILABILITY_REGISTRY_DATA](state): AvailabilityRegistryView {
            return state.availabilityRegistryView;
        },
        [ReportsManagementGetters.GET_INCIDENT_REGISTRY_VIEW](state): IncidentsView {
            return state.incidentRegistryView;
        },
        [ReportsManagementGetters.GET_HISTORY_REPORTS](state): ReportsHistoryDatatableItem[] {
            return state.historyView.reports.results.map(i => ({
                date: moment(i.date).format('DD/MM/YYYY hh:mm A'),
                parameter:
                    i.parameterEquipment.parameter.name +
                    ` (${i.parameterEquipment.parameter.measureUnit})`,
                value: parseFloat(i.value)
                    .toFixed(2)
                    .replace('.', ','),
                equipment: i.parameterEquipment.buildingEquipment.equipment.name,
                location: i.parameterEquipment.buildingEquipment.buildingDataCenter.dataCenter.name,
            }));
        },
        [ReportsManagementGetters.GET_HISTORY_INCIDENTS](state): IncidentsHistoryDatatableItem[] {
            return state.historyView.incidents.results.map(i => ({
                number: i.number,
                startDate: moment(i.startDate).format('DD/MM/YYYY hh:mm A'),
                endDate: moment(i.endDate).format('DD/MM/YYYY hh:mm A'),
                cause: i.incidentCause.name,
                location: i.dataCenter.name,
            }));
        },
        [ReportsManagementGetters.GET_HISTORY](state): HistoryState {
            return state.historyView;
        },
    },
    mutations: {
        [ReportsManagementMutations.SET_AVAILABILITY_REGISTRY_DATA](
            state,
            view: AvailabilityRegistryView,
        ): void {
            state.availabilityRegistryView = view;
        },
        [ReportsManagementMutations.SET_INCIDENT_REGISTRY_VIEW](state, view: IncidentsView): void {
            state.incidentRegistryView = view;
        },
        [ReportsManagementMutations.SET_HISTORY_REPORTS](state, reports) {
            state.historyView.reports = reports;
        },
        [ReportsManagementMutations.SET_HISTORY_INCIDENTS](state, incidents) {
            state.historyView.incidents = incidents;
        },
    },
    actions: {
        async [ReportsManagementActions.FETCH_AVAILABILITY_REGISTRY_DATA]({
            commit,
        }): Promise<RequestResponse> {
            try {
                const r = await reportsRepository.getInfoReports();
                commit(ReportsManagementMutations.SET_AVAILABILITY_REGISTRY_DATA, {
                    locations: r.reportsBasicInfo,
                });
                return false;
            } catch (e) {
                return new Exception(e.response);
            }
        },

        async [ReportsManagementActions.FETCH_INCIDENT_REGISTRY_VIEW]({ commit }) {
            try {
                const r = await reportsRepository.getInfoReports();
                commit(ReportsManagementMutations.SET_INCIDENT_REGISTRY_VIEW, {
                    locations: r.reportsBasicInfo,
                    causes: r.incidentCauses,
                });
                return false;
            } catch (e) {
                return new Exception(e.response);
            }
        },

        async [ReportsManagementActions.CREATE_AVAILABILITY_REPORT](
            {},
            params: {
                equipmentsData: EquipmentAvailability[];
                date: string;
            },
        ): Promise<RequestResponse> {
            try {
                const reports: Report[] = [];
                params.equipmentsData.forEach(equipment => {
                    for (const key in equipment) {
                        reports.push({
                            parameterEquipment: equipment[key].id,
                            value: equipment[key].value.replace(',', '.'),
                            date: params.date,
                        });
                    }
                });
                await reportsRepository.createAvailabilityReport(reports);
                return false;
            } catch (e) {
                return new Exception(e.response);
            }
        },

        async [ReportsManagementActions.CREATE_INCIDENT](
            {},
            incident: Incident,
        ): Promise<RequestResponse> {
            try {
                await incidentsRepository.createIncident(incident);
                return false;
            } catch (e) {
                return new Exception(e.response);
            }
        },

        async [ReportsManagementActions.FETCH_HISTORY_REPORTS](
            { commit },
            options: { limit: number; start: number },
        ): Promise<RequestResponse> {
            try {
                const reports = (await reportsRepository.listReports(options)).data;
                commit(ReportsManagementMutations.SET_HISTORY_REPORTS, reports);
                return false;
            } catch (e) {
                return new Exception(e.response);
            }
        },

        async [ReportsManagementActions.FETCH_HISTORY_INCIDENTS](
            { commit },
            options: { limit: number; start: number },
        ): Promise<RequestResponse> {
            try {
                const incidents = (await incidentsRepository.listIncidents(options)).data;
                commit(ReportsManagementMutations.SET_HISTORY_INCIDENTS, incidents);
                return false;
            } catch (e) {
                return new Exception(e.response);
            }
        },

        async [ReportsManagementActions.DOWNLOAD_REPORTS](
            {},
            data: { month: string; year: string; dataCenter: number },
        ): Promise<RequestResponse> {
            try {
                const report = await reportsRepository.getMonthlyReport(data);
                const filename = `reporte-mensual-(${data.year}-${data.month})-${
                    data.dataCenter === 1 ? 'CTDC' : 'CTDV'
                }.xls`;
                const type = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
                downloadFile(report.data, filename, type);
                return false;
            } catch (e) {
                return new Exception(e.response);
            }
        },

        async [ReportsManagementActions.DOWNLOAD_INCIDENTS](
            {},
            data: { dataCenter: number; year: string; summary: number },
        ): Promise<RequestResponse> {
            try {
                const report = await incidentsRepository.getIncidentsReport(data);
                let filename: string;
                const type = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
                if (data.summary) {
                    filename = `reporte-resumen_interrupciones-(${data.year})-${
                        data.dataCenter === 1 ? 'CTDC' : 'CTDV'
                    }.xls`;
                } else {
                    filename = `reporte-detalle_interrupciones-(${data.year})-${
                        data.dataCenter === 1 ? 'CTDC' : 'CTDV'
                    }.xls`;
                }
                downloadFile(report.data, filename, type);
                return false;
            } catch (e) {
                return new Exception(e.response);
            }
        },
    },
};

export default reportsManagement;
